import { useContext } from 'react';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';

import { Box, Button, Icon, Image, Table, Text } from '@zillow/constellation-10';
import { IconCheckmarkFilled } from '@zillow/constellation-icons';

import { PaymentGatewayContext } from '../PaymentGateway/context';
import { ModularListingContext } from '../../containers/CreateListingPage/context';
import { RMWContext } from '../../providers/RMWContext';

import PropertiesActions from '../../actions/PropertiesActions';
import { default as pgActions } from '../PaymentGateway/actions';
import actions from '../../containers/CreateListingPage/actions';

import PropertyConstants from '../../constants/PropertyConstants';
import { DISPLAY_CONTEXT } from '../PaymentGateway/constants';
import PayAndPublishCSEvents from '../../model/ClickstreamEvent/PayAndPublishCSEvents';
import { RMW_SUB_PATH } from '../../pathsManifest';

import PremiumBadge from '../../images/premium-badge.png';

import LearnAboutPremiumButton, { RENDER_AS_TYPE } from '../LearnAboutPremiumButton';

const propTypes = {};
const defaultProps = {};

const ProductFeatureCompareTable = () => {
  const dispatch = useDispatch();

  const {
    reducerDispatch: pgReducerDispatch,
    pgState: { displayPaymentGateway },
  } = useContext(PaymentGatewayContext);
  const { reducerDispatch: mlReducerDispatch } = useContext(ModularListingContext);
  const { paths } = useContext(RMWContext);

  const { currentProperty, products, listingAlias, user } = useSelector(
    (state) => ({
      currentProperty: state.properties.currentProperty,
      products: state.properties.currentProperty.freemium.products,
      listingAlias: state.properties.currentProperty.listingAlias,
      user: state.user,
    }),
    shallowEqual,
  );

  const freePrice = products[0]?.price || 0;
  const premiumPrice = products[1]?.price || 0;

  const updateDisplayPaymentGateway = (value) => {
    pgActions.updateAllData({
      reducerDispatch: pgReducerDispatch,
      data: {
        displayPaymentGateway: value,
        displayContext: DISPLAY_CONTEXT.MODULAR_LISTING,
      },
    });
  };

  const updateSelectedProductType = (selectedProductType) => {
    dispatch(
      PropertiesActions.updateCurrentProperty({
        selectedProductType,
      }),
    );
  };

  const handlePublishClick = (isPremium) => {
    if (isPremium) {
      PayAndPublishCSEvents.StartActivatePremiumBottom({ currentProperty, user });
    } else {
      PayAndPublishCSEvents.StartActivateFreemiumBottom({ currentProperty, user });
    }

    updateSelectedProductType(
      isPremium ? PropertyConstants.PRODUCT_TYPE_NAME_TO_ID.PREMIUM : PropertyConstants.PRODUCT_TYPE_NAME_TO_ID.FREE,
    );

    const shouldDisplayPaymentGateway = isPremium;
    updateDisplayPaymentGateway(shouldDisplayPaymentGateway);

    if (!shouldDisplayPaymentGateway) {
      actions.updateWizardComplete({ reducerDispatch: mlReducerDispatch, isWizardComplete: true });
      window.router.transitionTo(`${paths.propertiesPage}/${listingAlias}/${RMW_SUB_PATH.DASHBOARD}/overview`);
    }
  };

  const renderPremiumOption = () => {
    return (
      <Box
        css={{
          position: 'relative',
          paddingBottom: '44px',
          textAlign: 'left',
          height: '100%',
        }}
      >
        <Text textStyle="body-bold" asChild css={{ marginBottom: '400' }}>
          <p>Get the most out of your listing</p>
        </Text>
        <Text asChild>
          <p>
            Get more exposure on your listing and gain access to deeper insights and analytics to help you price your
            rental and optimize your listing.
          </p>
        </Text>

        <div>
          <Box css={{ display: 'flex', alignItems: 'center', flexDirection: 'row', justifyContent: 'space-between' }}>
            <div>
              <Text textStyle="heading-xs" asChild>
                <p>{premiumPrice}</p>
              </Text>
              <Text textStyle="body-sm" color="text.subtle" asChild>
                <p>One-time payment</p>
              </Text>
            </div>

            <Image src={PremiumBadge} alt="premium badge" css={{ width: '88px', marginRight: '-2px' }} />
          </Box>
        </div>

        <Button
          fluid={true}
          tone="brand"
          emphasis="filled"
          css={{ position: 'absolute', bottom: 0 }}
          onClick={() => handlePublishClick(true)}
          data-eventid={PayAndPublishCSEvents.StartActivatePremiumBottom.data_event_id}
        >
          Upgrade to premium
        </Button>
      </Box>
    );
  };

  const renderFreeOption = () => {
    return (
      <Box
        css={{
          position: 'relative',
          paddingBottom: '44px',
          textAlign: 'left',
          height: '100%',
        }}
      >
        <Text textStyle="body-bold" asChild css={{ marginBottom: '400' }}>
          <p>Use our basic listing services</p>
        </Text>
        <Text asChild css={{ marginBottom: '400' }}>
          <p>
            List your property where millions of renters search each month and access our suite of landlord tools to
            screen tenants, sign a lease, and collect rent online.
          </p>
        </Text>

        <Text textStyle="heading-xs" asChild>
          <p>{freePrice}</p>
        </Text>

        <Button
          fluid={true}
          tone="brand"
          emphasis="outlined"
          disabled={displayPaymentGateway}
          loading={displayPaymentGateway}
          css={{ position: 'absolute', bottom: 0 }}
          onClick={() => handlePublishClick(false)}
          data-eventid={PayAndPublishCSEvents.StartActivateFreemiumBottom.data_event_id}
        >
          Continue with free listing
        </Button>
      </Box>
    );
  };

  return (
    <Table.Root
      appearance="horizontal"
      size="md"
      alignments={['left', 'center', 'center']}
      data-testid="ProductFeatureCompareTable"
    >
      <Table.Header>
        <Table.Row>
          <Table.HeaderCell css={{ width: 'inherit', lg: { width: '348px' }, xxl: { width: '370px' } }} />
          <Table.HeaderCell
            css={{
              backgroundColor: 'blue.100',
              width: 'inherit',
              lg: { width: '348px' },
              xxl: { width: '370px' },
            }}
          >
            <Text css={{ textStyle: { base: 'body-xs-bold', xl: 'heading-xs' } }}>Premium</Text>
          </Table.HeaderCell>
          <Table.HeaderCell css={{ width: 'inherit', lg: { width: '348px' }, xxl: { width: '370px' } }}>
            <Text css={{ textStyle: { base: 'body-xs-bold', xl: 'heading-xs' } }}>Basic</Text>
          </Table.HeaderCell>
        </Table.Row>
      </Table.Header>

      <Table.Body>
        <Table.Row>
          <Table.Cell css={{ paddingLeft: '0' }}>
            <Text css={{ textStyle: { base: 'body-sm', xl: 'body-bold' } }}>
              Syndication to Trulia, HotPads, and Zillow
            </Text>
          </Table.Cell>
          <Table.Cell css={{ backgroundColor: 'blue.100' }}>
            <Icon size="md" css={{ color: 'icon.accent.brand.hero' }} render={<IconCheckmarkFilled />} />
          </Table.Cell>
          <Table.Cell>
            <Icon size="md" css={{ color: 'icon.action.neutral.default' }} render={<IconCheckmarkFilled />} />
          </Table.Cell>
        </Table.Row>

        <Table.Row>
          <Table.Cell css={{ paddingLeft: '0' }}>
            <Text css={{ textStyle: { base: 'body-sm', xl: 'body-bold' } }}>Applications</Text>
          </Table.Cell>
          <Table.Cell css={{ backgroundColor: 'blue.100' }}>
            <Icon size="md" css={{ color: 'icon.accent.brand.hero' }} render={<IconCheckmarkFilled />} />
          </Table.Cell>
          <Table.Cell>
            <Icon size="md" css={{ color: 'icon.action.neutral.default' }} render={<IconCheckmarkFilled />} />
          </Table.Cell>
        </Table.Row>

        <Table.Row>
          <Table.Cell css={{ paddingLeft: '0' }}>
            <Text css={{ textStyle: { base: 'body-sm', xl: 'body-bold' } }}>Online lease signing</Text>
          </Table.Cell>
          <Table.Cell css={{ backgroundColor: 'blue.100' }}>
            <Icon size="md" css={{ color: 'icon.accent.brand.hero' }} render={<IconCheckmarkFilled />} />
          </Table.Cell>
          <Table.Cell>
            <Icon size="md" css={{ color: 'icon.action.neutral.default' }} render={<IconCheckmarkFilled />} />
          </Table.Cell>
        </Table.Row>

        <Table.Row>
          <Table.Cell css={{ paddingLeft: '0' }}>
            <Text css={{ textStyle: { base: 'body-sm', xl: 'body-bold' } }}>Lease builder (in select markets)</Text>
          </Table.Cell>
          <Table.Cell css={{ backgroundColor: 'blue.100' }}>
            <Icon size="md" css={{ color: 'icon.accent.brand.hero' }} render={<IconCheckmarkFilled />} />
          </Table.Cell>
          <Table.Cell>
            <Icon size="md" css={{ color: 'icon.action.neutral.default' }} render={<IconCheckmarkFilled />} />
          </Table.Cell>
        </Table.Row>

        <Table.Row>
          <Table.Cell css={{ paddingLeft: '0' }}>
            <Text css={{ textStyle: { base: 'body-sm', xl: 'body-bold' } }}>Online payments</Text>
          </Table.Cell>
          <Table.Cell css={{ backgroundColor: 'blue.100' }}>
            <Icon size="md" css={{ color: 'icon.accent.brand.hero' }} render={<IconCheckmarkFilled />} />
          </Table.Cell>
          <Table.Cell>
            <Icon size="md" css={{ color: 'icon.action.neutral.default' }} render={<IconCheckmarkFilled />} />
          </Table.Cell>
        </Table.Row>

        <Table.Row>
          <Table.Cell css={{ paddingLeft: '0' }}>
            <Text css={{ textStyle: { base: 'body-sm', xl: 'body-bold' } }}>App / messaging platform</Text>
          </Table.Cell>
          <Table.Cell css={{ backgroundColor: 'blue.100' }}>
            <Icon size="md" css={{ color: 'icon.accent.brand.hero' }} render={<IconCheckmarkFilled />} />
          </Table.Cell>
          <Table.Cell>
            <Icon size="md" css={{ color: 'icon.action.neutral.default' }} render={<IconCheckmarkFilled />} />
          </Table.Cell>
        </Table.Row>

        <Table.Row>
          <Table.Cell css={{ paddingLeft: '0' }}>
            <Text css={{ textStyle: { base: 'body-sm', xl: 'body-bold' } }}>More exposure</Text>
          </Table.Cell>
          <Table.Cell css={{ backgroundColor: 'blue.100' }}>
            <Icon size="md" css={{ color: 'icon.accent.brand.hero' }} render={<IconCheckmarkFilled />} />
          </Table.Cell>
          <Table.Cell />
        </Table.Row>

        <Table.Row>
          <Table.Cell css={{ paddingLeft: '0' }}>
            <Text css={{ textStyle: { base: 'body-sm', xl: 'body-bold' } }}>Listing recommendations</Text>
          </Table.Cell>
          <Table.Cell css={{ backgroundColor: 'blue.100' }}>
            <Icon size="md" css={{ color: 'icon.accent.brand.hero' }} render={<IconCheckmarkFilled />} />
          </Table.Cell>
          <Table.Cell />
        </Table.Row>

        <Table.Row>
          <Table.Cell css={{ paddingLeft: '0' }}>
            <Text css={{ textStyle: { base: 'body-sm', xl: 'body-bold' } }}>Pricing &amp; market insights</Text>
          </Table.Cell>
          <Table.Cell css={{ backgroundColor: 'blue.100' }}>
            <Icon size="md" css={{ color: 'icon.accent.brand.hero' }} render={<IconCheckmarkFilled />} />
          </Table.Cell>
          <Table.Cell />
        </Table.Row>

        <Table.Row>
          <Table.Cell css={{ paddingLeft: '0' }}>
            <Text css={{ textStyle: { base: 'body-sm', xl: 'body-bold' } }}>Priority support</Text>
          </Table.Cell>
          <Table.Cell css={{ backgroundColor: 'blue.100' }}>
            <Icon size="md" css={{ color: 'icon.accent.brand.hero' }} render={<IconCheckmarkFilled />} />
          </Table.Cell>
          <Table.Cell />
        </Table.Row>

        <Box css={{ display: 'none', xxl: { display: 'contents' } }}>
          <Table.Row>
            <Table.Cell css={{ borderBottom: '0', paddingLeft: '0' }}>
              <LearnAboutPremiumButton as={RENDER_AS_TYPE.TEXT_BUTTON}>
                Learn more about premium
              </LearnAboutPremiumButton>
            </Table.Cell>
            <Table.Cell css={{ borderBottom: '0', backgroundColor: 'blue.100', height: '270px' }}>
              {renderPremiumOption()}
            </Table.Cell>
            <Table.Cell css={{ borderBottom: '0', height: '270px' }}>{renderFreeOption()}</Table.Cell>
          </Table.Row>
        </Box>
      </Table.Body>
    </Table.Root>
  );
};

ProductFeatureCompareTable.propTypes = propTypes;
ProductFeatureCompareTable.defaultProps = defaultProps;

export default ProductFeatureCompareTable;
